import { debugLog } from './debugLog';

// Environment variable validation utilities
export const getRequiredEnvVar = (name: string): string => {
    const value = process.env[name];
    if (!value) {
        throw new Error(`Environment variable ${name} is required but not set.`);
    }
    return value;
};

export const getOptionalEnvVar = (name: string, defaultValue: string = ""): string => {
    return process.env[name] || defaultValue;
};

export const EMPTY_MSAL_CLIENT_ID = '00000000-0000-0000-0000-000000000000';

export const normalizeMsalClientId = (raw: string | null | undefined): string => {
    return String(raw || '').trim();
};

export const isMsalClientIdConfigured = (raw: string | null | undefined): boolean => {
    const clientId = normalizeMsalClientId(raw);
    return clientId.length > 0 && clientId !== EMPTY_MSAL_CLIENT_ID;
};

export const isMsalConfigured = (): boolean =>
    isMsalClientIdConfigured(process.env.REACT_APP_MSAL_AUTH_CLIENT_ID);

export interface MsalConfigDiagnostics {
    rawClientId: string;
    normalizedClientId: string;
    clientIdLength: number;
    isConfigured: boolean;
    isPlaceholderClientId: boolean;
    authorityEnv: string;
    domainHintEnv: string;
    customScopeEnv: string;
    appEnv: string;
    storybook: string;
    locationHref?: string;
}

export const getMsalConfigDiagnostics = (): MsalConfigDiagnostics => {
    const rawClientId = String(process.env.REACT_APP_MSAL_AUTH_CLIENT_ID || '');
    const normalizedClientId = normalizeMsalClientId(rawClientId);
    return {
        rawClientId,
        normalizedClientId,
        clientIdLength: normalizedClientId.length,
        isConfigured: isMsalClientIdConfigured(normalizedClientId),
        isPlaceholderClientId: normalizedClientId === EMPTY_MSAL_CLIENT_ID,
        authorityEnv: String(process.env.REACT_APP_MSAL_AUTH_AUTHORITY || ''),
        domainHintEnv: String(process.env.REACT_APP_MSAL_DOMAIN_HINT || ''),
        customScopeEnv: String(process.env.REACT_APP_MSAL_CUSTOM_SCOPE || ''),
        appEnv: String(process.env.REACT_APP_ENV || ''),
        storybook: String(process.env.STORYBOOK || ''),
        locationHref: typeof window !== 'undefined' ? window.location.href : undefined
    };
};

export const logMsalConfigDiagnostics = (context: string, extra?: Record<string, unknown>): void => {
    if (process.env.REACT_APP_MSAL_DIAG !== 'true') {
        return;
    }
    debugLog(`[MSAL DIAG] ${context}`, {
        ...getMsalConfigDiagnostics(),
        ...(extra || {})
    });
};

// Environment detection
export const getEnvironmentConfig = () => {
    const env = getOptionalEnvVar('REACT_APP_ENV', 'DEV');
    return {
        isProduction: env === 'PROD',
        environment: env
    };
};

/** True when running in Storybook or when MSAL env is not set (allows components to load without auth). */
export const isNoAuthMode = (): boolean =>
    !isMsalConfigured() || process.env.STORYBOOK === 'true';

// Azure AD specific environment variables
export const getAzureConfig = () => {
    const clientId = normalizeMsalClientId(process.env.REACT_APP_MSAL_AUTH_CLIENT_ID);
    const tenantId = getOptionalEnvVar('REACT_APP_MSAL_AUTH_AUTHORITY', 'duvalschoolsorg.onmicrosoft.com');
    const domainHint = getOptionalEnvVar('REACT_APP_MSAL_DOMAIN_HINT', 'duvalschools.org');

    // Get environment config for conditional defaults
    const { isProduction } = getEnvironmentConfig();

    // Different default scopes based on environment
    const defaultScopes = isProduction
        ? 'User.Read,openid,profile,offline_access' // More restrictive in production
        : 'User.Read,User.ReadWrite.All,openid,profile,offline_access'; // Full permissions in development

    // Check if custom scopes are provided
    const customScopes = getOptionalEnvVar('REACT_APP_MSAL_CUSTOM_SCOPE', '');

    // Use custom scopes if provided, otherwise use defaults
    const scopesString = customScopes || defaultScopes;
    const scopes = scopesString.split(',').map(scope => scope.trim()).filter(scope => scope.length > 0);

    return {
        clientId,
        tenantId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        domainHint,
        scopes,
        isConfigured: isMsalClientIdConfigured(clientId)
    };
};

// SCIM API configuration
export const getScimConfig = () => {
    // Get environment config for conditional defaults
    const { isProduction } = getEnvironmentConfig();

    // Different default API URLs based on environment
    const defaultApiUrl = isProduction
        ? 'https://dcps-core-api.azurewebsites.net/api/scim/v2/' // Production API
        : 'https://dcps-core-api-dev.azurewebsites.net/api/scim/v2/'; // Development API

    return {
        apiUrl: getOptionalEnvVar('REACT_APP_SCIM_API_URL', defaultApiUrl)
    };
}; 